import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Flame, GraduationCap, Lock } from "lucide-react";
import { API, errText, useAuth } from "@/lib/auth";

export const useMode = () => {
  const { profile } = useAuth();
  const [d, setD] = useState(null);
  const load = () => axios.get(`${API}/me/mode`).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { if (profile) load(); else setD(null); }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { window.addEventListener("paper-changed", load); return () => window.removeEventListener("paper-changed", load); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [d, setD];
};

const Progress = ({ have, need }) => (
  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-1.5"><div className="h-full bg-[var(--cyan)]" style={{ width: `${Math.min(100, (have / need) * 100)}%` }} /></div>
);

export const ModeCard = ({ compact }) => {
  const [d, setD] = useMode();
  if (!d) return null;
  const pick = async (mode) => {
    try { const { data } = await axios.post(`${API}/me/mode`, { mode }); setD(data); toast.success(`${data.modes[mode].name} mode on`); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <section className="glass p-5 space-y-3" data-testid="mode-card">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <h2 className="font-head font-semibold flex items-center gap-2"><GraduationCap className="w-4 h-4 text-[var(--cyan)]" />Your mode: <span className="text-[var(--cyan)]" data-testid="mode-current">{d.modes[d.mode].name}</span></h2>
        <span className="flex items-center gap-1 text-xs text-amber-300 font-num" data-testid="mode-streak"><Flame className="w-3.5 h-3.5" />{d.stats.streak} win streak · best {d.stats.best_streak}</span>
      </div>
      {!compact && <p className="text-xs text-slate-400">{d.modes[d.mode].summary}</p>}
      <div className="grid sm:grid-cols-3 gap-2">
        {Object.entries(d.modes).map(([id, m]) => {
          const locked = !d.unlocked[id];
          const need = id === "intermediate" ? 20 : 50;
          return (
            <button key={id} onClick={() => !locked && pick(id)} data-testid={`mode-${id}`}
              className={`rounded-xl border p-3 text-left text-xs transition-colors ${d.mode === id ? "border-[var(--cyan)] bg-[var(--cyan)]/10" : "border-[var(--line)] hover:border-[var(--cyan)]/60"} ${locked ? "opacity-70 cursor-default" : ""}`}>
              <div className="flex items-center justify-between font-semibold text-sm">{m.name}{locked && <Lock className="w-3.5 h-3.5 text-slate-500" />}</div>
              {locked ? <><div className="text-slate-500 mt-1">Unlock: {d.requirements[id]}</div><Progress have={d.stats.closed} need={need} />
                <div className="text-[10px] text-slate-500 mt-1 font-num">{d.stats.closed}/{need} bets · P&L ${d.stats.pnl}</div></> :
                <div className="text-slate-400 mt-1">Up to ${m.max_bet} per bet</div>}
            </button>);
        })}
      </div>
    </section>
  );
};
